import React, { useEffect, useRef, useState } from "react";

/**
 * A row of single-character code boxes.
 *
 * Owns its own digits so a partially filled code (e.g. box 1 and 3) keeps each
 * character in the box it was typed into; `onChange` reports the joined string,
 * which is shorter than `length` until every box is filled.
 *
 * Bump `resetSignal` to clear the boxes and refocus the first one.
 */
export default function OtpInput({
  length = 6,
  onChange,
  disabled = false,
  resetSignal = 0,
  autoFocus = true,
}) {
  const [digits, setDigits] = useState(() => Array(length).fill(""));
  const inputRefs = useRef([]);

  useEffect(() => {
    if (autoFocus) inputRefs.current[0]?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (!resetSignal) return;
    setDigits(Array(length).fill(""));
    inputRefs.current[0]?.focus();
  }, [resetSignal, length]);

  const emit = (next) => {
    setDigits(next);
    onChange(next.join(""));
  };

  const setDigitAt = (index, value) => {
    const next = [...digits];
    next[index] = value;
    emit(next);
  };

  const fillFrom = (rawValue, startIndex) => {
    const chars = rawValue
      .replace(/\D/g, "")
      .slice(0, length - startIndex)
      .split("");

    if (!chars.length) return;

    const next = [...digits];
    chars.forEach((char, i) => {
      next[startIndex + i] = char;
    });
    emit(next);

    const lastFilled = Math.min(startIndex + chars.length, length - 1);
    inputRefs.current[lastFilled]?.focus();
  };

  const handleChange = (index, rawValue) => {
    const value = rawValue.replace(/\D/g, "");

    if (!value) {
      setDigitAt(index, "");
      return;
    }

    // Typing over a filled box, or a browser autofilling the whole code.
    if (value.length > 1) {
      fillFrom(value, index);
      return;
    }

    setDigitAt(index, value);
    if (index < length - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (digits[index]) {
        setDigitAt(index, "");
      } else if (index > 0) {
        setDigitAt(index - 1, "");
        inputRefs.current[index - 1]?.focus();
      }
      return;
    }

    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    fillFrom(e.clipboardData.getData("text"), 0);
  };

  return (
    <div className="otp-inputs" onPaste={handlePaste}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={(e) => e.target.select()}
          className={`otp-input${digit ? " is-filled" : ""}`}
          aria-label={`Digit ${index + 1} of ${length}`}
        />
      ))}
    </div>
  );
}
